import { LogService } from "@bitwarden/common/abstractions/log.service";
import { DateFormatPipe } from "@bitwarden/web-vault/app/pipes/date-format.pipe";
import { IReferenceData } from "@bitwarden/web-vault/app/models/view/reference-data/reference-data.view";
import { RefDataProperty } from "@bitwarden/web-vault/app/models/types/revaluation.types";
import { IntervalTree } from "./interval-tree.class";
import { Graph } from "./graph.class";

type Edge = {
  symbol: string;
  base: string;
  rate: number;
};

/**
 * Normalize a raw instant to the UTC-midnight epoch of the calendar day it falls in, in the
 * runtime's local timezone — the same day-boundary convention getRefData() already uses (via
 * DateFormatPipe), and the same convention every reference-data record's own tree timestamp
 * implicitly has (a bare "yyyy-MM-dd" string always parses as UTC midnight). Without this,
 * comparing a raw instant directly against those timestamps can treat a reference record dated
 * the transaction's own calendar day as "in the future" whenever the caller's timezone sits ahead
 * of UTC (see ISSUE 4 in revaluation.spec.ts) — ReferenceDataGraph.calculateRate() then falls back
 * to a stale, older rate instead of the correct same-day one.
 */
function toLocalDayEpoch(date: number): number {
  const isoDay = new DateFormatPipe().transform(new Date(date));
  return new Date(isoDay).getTime();
}

/**
 * Ascending comparator by `date.date` epoch — the "reference data is ordered by date" convention
 * shared by `getRefData()` below and `RevaluationProcessHelper.bucketReferencesByGranularity()`.
 * Kept in one place so both call sites can't drift out of sync.
 */
export function byRefDateAsc(a: IReferenceData, b: IReferenceData): number {
  return new Date(a.date.date).getTime() - new Date(b.date.date).getTime();
}

// TODO: This class is designed to handle the symbol converison
// like refenrece data are USD/HKD, HKD/EUR, EUR/SGD and find the rate between USD and SGD.
// In our system, we already have USD/SGD if the user prefered currency is SGD.
// We could make this class more simple.
class ReferenceDataGraph {
  private tree: IntervalTree<Edge>;

  constructor(referenceData: IReferenceData[]) {
    this.tree = new IntervalTree<Edge>();
    this.buildTree(referenceData);
  }

  /**
   * Build the graph by inserting edges into the AVL tree.
   */
  private buildTree(data: IReferenceData[]): void {
    data.forEach(({ symbol, base, rate, date }) => {
      if (!symbol || !base || !rate || rate <= 0) {
        return;
      }

      const timestamp = new Date(date.date).getTime();
      this.tree.insert(timestamp, timestamp, { symbol, base, rate });
    });
  }

  /**
   * Build the adjacency list for the graph based on the edges.
   */
  private buildGraph(): Graph<number, { value: number; key: string }> {
    return new Graph<number, { value: number; key: string }>();
  }

  private startCalculation(
    graph: Graph<number, { value: number; key: string }>,
    edges: Edge[],
    quote: string,
    base: string,
  ): number | null {
    if (edges.length === 0) {
      return null;
    }

    edges.forEach(({ symbol, base, rate }) => {
      graph.addEdge(base, { key: symbol, value: rate });
      graph.addEdge(symbol, { key: base, value: 1 / rate });
    });

    return graph.bfs(
      quote,
      base,
      (value, node) => {
        return node.value * value;
      },
      1,
    );
  }

  /**
   * Calculate the rate between two currencies for a given date.
   * @param allowForward when false, only rates on or before `date` are considered — the caller
   * gets `null` instead of a rate that only exists after `date`. Callers computing a value as-of
   * a specific date (e.g. revaluation) must never be handed a rate from the future.
   *
   * `date` is normalized to its own local calendar day (see `toLocalDayEpoch()`) before being
   * compared against the tree — every reference record's own timestamp is implicitly a
   * calendar-day label (UTC midnight), so comparing a raw, un-normalized instant against those
   * would wrongly exclude same-day records whenever the caller's timezone sits ahead of UTC. This
   * is the single delegation point for both of `calculateRate()`'s real callers
   * (`RevaluationProcess.buildCurrencyRef()` and `SymbolConversion.normalizeQuantity()`), so
   * normalizing here guarantees they agree on what rate applies to a given date — see ISSUE 4 in
   * revaluation.spec.ts.
   */
  calculateRate(quote: string, base: string, date: number, allowForward = true): number | null {
    if (quote === base) {
      return 1;
    }

    const normalizedDate = toLocalDayEpoch(date);
    const graph = this.buildGraph();
    // if put 0 as the start date
    // it will associate with all the data all the way to the end date
    let edges = this.tree.query(0, normalizedDate, (a, b) => b.end - a.end); // descending order
    const rate = this.startCalculation(graph, edges, quote, base);

    // if not found, try to find the next available rate after the given date
    // if this happens a lot, rather saving the rates on those date
    if (rate != null) {
      return rate;
    }

    if (!allowForward) {
      return null;
    }

    edges = this.tree.query(normalizedDate + 1, Number.MAX_SAFE_INTEGER, (a, b) => a.end - b.end); // ascending order
    return this.startCalculation(graph, edges, quote, base);
  }
}

export class ReferenceDataCalculationClass {
  private logger: LogService;
  private allReferenceData: Array<IReferenceData>;
  private globalReserveCurrency = "USD";

  constructor(logger: LogService, referenceData: IReferenceData[]) {
    this.logger = logger;
    this.allReferenceData = referenceData || [];
  }

  calculateRate(quote: string, base: string, date: number, allowForward = true): number | null {
    const graph = new ReferenceDataGraph(this.allReferenceData);
    return graph.calculateRate(quote, base, date, allowForward); // Delegate to the graph
  }

  /**
   * Given a symbol, base and date, retrieve the reference data for that record
   */
  getReferenceData(symbol: string, base: string, startDate: number): Array<IReferenceData> {
    this.logger.debug("ReferenceDataCalculationService: getReferenceData");
    const referenceData = [];

    const closestReference = this.filterLastPriceDate(startDate, (symbolData) => {
      const baseMatch = base === symbolData.base;
      const symbolMatch = symbolData.symbol === symbol;

      return symbolMatch && baseMatch;
    });

    if (closestReference) {
      referenceData.push(closestReference);
    }

    return referenceData;
  }

  /**
   * return the reference data for the given symbol, base, from date and to date
   */
  getRefData(refMetaData: RefDataProperty): IReferenceData[] {
    const { symbol, fromDate, toDate, base } = refMetaData;
    const isoFromDate = new DateFormatPipe().transform(fromDate);
    const unixFromDate = new Date(isoFromDate).getTime();

    const isoToDate = new DateFormatPipe().transform(toDate);
    const unixToDate = new Date(isoToDate).getTime();

    return this.allReferenceData
      .filter((reference) => {
        const baseMatch = base === reference.base;
        const symbolMatch = reference.symbol === symbol;

        const isoRefDate = new DateFormatPipe().transform(new Date(reference.date.date));
        const unixRefDate = new Date(isoRefDate).getTime();

        const dateMatch = unixFromDate <= unixRefDate && unixToDate >= unixRefDate;

        return symbolMatch && baseMatch && dateMatch;
      })
      .sort(byRefDateAsc);
  }

  /**
   * Given a symbol, base retrieve all the reference data that matches
   */
  getReferenceForSymbolDate(symbol: string, startDate: number): IReferenceData[] {
    const referenceData: IReferenceData[] = [];
    const possibleBases: string[] = [];
    this.allReferenceData.forEach((reference) => {
      if (reference.symbol === symbol && !possibleBases.includes(reference.base)) {
        possibleBases.push(reference.base);
      }
    });

    possibleBases.forEach((base) => {
      const closestReference = this.filterLastPriceDate(startDate, (symbolData) => {
        const symbolMatch = symbolData.symbol === symbol;
        const baseMatch = symbolData.base === base;
        return symbolMatch && baseMatch;
      });
      if (closestReference) {
        referenceData.push(closestReference);
      }
    });
    return referenceData;
  }

  /**
   * Given a symbol retrieve all the reference data that matches this as the base
   */
  getReferenceForBaseDate(base: string, startDate: number): IReferenceData[] {
    const referenceData: IReferenceData[] = [];
    const possibleSymbols: string[] = [];
    this.allReferenceData.forEach((reference) => {
      if (reference.base === base && !possibleSymbols.includes(reference.symbol)) {
        possibleSymbols.push(reference.symbol);
      }
    });

    possibleSymbols.forEach((symbol) => {
      const closestReference = this.filterLastPriceDate(startDate, (symbolData) => {
        const symbolMatch = symbolData.symbol === symbol;
        const baseMatch = symbolData.base === base;
        return symbolMatch && baseMatch;
      });
      if (closestReference) {
        referenceData.push(closestReference);
      }
    });
    return referenceData;
  }

  /**
   * Note that we currently assume that the most recent reference data to the date is the most accurate.
   * However, in the future the most accurate one might be the one that produces the greatest base value
   * when normalized unique on currency symbol.
   *
   * @param date
   * @param fn
   * @private
   */
  private filterLastPriceDate(date: number, fn: (data: IReferenceData) => boolean): IReferenceData {
    const pipe = new DateFormatPipe();
    let lowerBoundValue = 0;
    let upperBoundValue = Number.MAX_SAFE_INTEGER;
    let lowerBound: IReferenceData;
    let upperBound: IReferenceData;
    const estimatedVaule = date;
    const data = this.allReferenceData;

    for (let i = 0; i < data.length; i++) {
      const di = data[i];

      if (!fn(di)) {
        continue;
      }

      const n = new Date(pipe.transform(di.date.date ?? undefined)).getTime();
      if (n === estimatedVaule) {
        lowerBound = di;
        upperBound = di;
        break;
      }

      if (n <= upperBoundValue && estimatedVaule < n) {
        upperBoundValue = n;
        upperBound = di;
      } else if (n >= lowerBoundValue && n < estimatedVaule) {
        lowerBoundValue = n;
        lowerBound = di;
      }
    }
    return lowerBound ?? upperBound;
  }
}
